import { computed } from 'vue';
import { useGlobalPermission } from './usePermission';
import { extractAllPermissionIds } from '@/utils/routeFilter';
import { modulePermissionMap } from '@/constants/permissions';

/**
 * 選單管理 composable
 * 提供基於權限的選單資料
 */
export function useMenu() {
    const { getUserPermissions } = useGlobalPermission();

    /**
     * 獲取使用者權限 ID 清單
     */
    const getUserPermissionIds = () => {
        const permissions = getUserPermissions();
        if (!permissions) return [];
        const ids = extractAllPermissionIds(permissions);
        return ids;
    };

    /**
     * 檢查模組是否有權限
     * @param {string} modulePath - 模組路徑
     * @returns {boolean} 是否有權限
     */
    const hasModulePermission = (modulePath) => {
        const permissionIds = getUserPermissionIds();
        const moduleConfig = modulePermissionMap[modulePath];

        if (!moduleConfig) return false;

        const folderId = moduleConfig.folder;
        if (folderId === null || folderId === undefined) return true;
        return permissionIds.includes(folderId);
    };

    /**
     * 檢查子路由是否有權限
     * @param {string} modulePath - 模組路徑
     * @param {string} childName - 子路由名稱
     * @returns {boolean} 是否有權限
     */
    const hasChildPermission = (modulePath, childName) => {
        const permissionIds = getUserPermissionIds();
        const moduleConfig = modulePermissionMap[modulePath];

        if (!moduleConfig || !moduleConfig.children[childName]) return false;

        const childIds = moduleConfig.children[childName];
        const hasRealIds = childIds.some(
            (id) => id !== null && id !== undefined
        );
        if (!hasRealIds) return true;
        return childIds.some((id) => permissionIds.includes(id));
    };

    /**
     * 過濾有權限的選單項目
     * @param {Array} menuItems - 選單項目清單
     * @returns {Array} 過濾後的選單項目
     */
    const filterMenuItems = (menuItems) => {
        return menuItems.filter((item) => {
            // 檢查模組權限
            if (!hasModulePermission(item.path)) {
                return false;
            }

            // 過濾子選單
            if (item.children && item.children.length > 0) {
                const authorizedChildren = item.children.filter((child) =>
                    hasChildPermission(item.path, child.name)
                );

                if (authorizedChildren.length === 0) {
                    return false;
                }

                // 更新子選單
                item.children = authorizedChildren;
            }

            return true;
        });
    };

    /**
     * 獲取選單資料
     * @param {Array} modulesConfig - 模組配置
     * @returns {Array} 選單資料
     */
    const getMenuData = (modulesConfig) => {
        const permissionIds = getUserPermissionIds();

        if (permissionIds.length === 0) {
            console.warn('未找到使用者權限資料');
            return [];
        }

        // 過濾有權限的模組
        const authorizedModules = modulesConfig
            .map(({ folder, children }) => {
                const modulePath = folder.path;
                const moduleConfig = modulePermissionMap[modulePath];

                const folderId = moduleConfig?.folder;
                const hasFolderPermission =
                    folderId === null ||
                    folderId === undefined ||
                    permissionIds.includes(folderId);

                if (!moduleConfig || !hasFolderPermission) {
                    return null;
                }

                // 如果是單層結構（沒有子路由），直接返回模組
                if (children.length === 0) {
                    return {
                        ...folder,
                        children: []
                    };
                }

                // 過濾有權限的子路由
                const authorizedChildren = children.filter((child) => {
                    if (child.redirect) {
                        return false;
                    }
                    // 如果設定了 hideInMenu，則不在選單中顯示
                    if (child.meta && child.meta.hideInMenu) {
                        return false;
                    }
                    if (!child.meta?.title) {
                        return false;
                    }

                    const childPermissions = moduleConfig.children[child.name];
                    if (!childPermissions) {
                        // 如果沒有權限配置，預設允許（用於編輯頁面等）
                        return true;
                    }

                    return childPermissions.some((permissionId) =>
                        permissionIds.includes(permissionId)
                    );
                });

                if (authorizedChildren.length === 0) {
                    return null;
                }

                return {
                    ...folder,
                    children: authorizedChildren
                };
            })
            .filter(Boolean);

        return authorizedModules;
    };

    /**
     * 檢查選單項目是否可見
     * @param {Object} menuItem - 選單項目
     * @returns {boolean} 是否可見
     */
    const isMenuItemVisible = (menuItem) => {
        if (!menuItem.path) return true;

        return hasModulePermission(menuItem.path);
    };

    /**
     * 檢查子選單項目是否可見
     * @param {string} modulePath - 模組路徑
     * @param {Object} childItem - 子選單項目
     * @returns {boolean} 是否可見
     */
    const isChildMenuItemVisible = (modulePath, childItem) => {
        return hasChildPermission(modulePath, childItem.name);
    };

    return {
        // 權限檢查
        hasModulePermission,
        hasChildPermission,
        isMenuItemVisible,
        isChildMenuItemVisible,

        // 選單資料
        getMenuData,
        filterMenuItems,

        // 權限資料
        getUserPermissionIds
    };
}
